import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axiosInstance';
import { setSelectedStore } from './storeSlice';
import { setCurrentStore, initializeStoreContext } from '../utils/storeContext';

export const signup = createAsyncThunk('auth/signup', async (data) => {
  const response = await axiosInstance.post('/auth/signup', data);
  return response.data;
});

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      // For employee login, set the store ID in headers BEFORE making the request
      if (!credentials.isOwner && credentials.storeId) {
        // Set the header for this specific request
        const config = {
          headers: {
            'x-storeid': credentials.storeId
          }
        };
        
        const response = await axiosInstance.post('/auth/login', credentials, config);
        const user = response.data;



        
        // After successful login, set the header for future requests
        // For employees, we need to get the actual MongoDB _id from the user's store
        if (user.store && user.store._id) {
          axiosInstance.defaults.headers.common['x-storeid'] = user.store._id;
          setCurrentStore(user.store);
        } else if (user.store && user.store.storeId) {
          // If we have storeId but not _id, we need to get the _id
          axiosInstance.defaults.headers.common['x-storeid'] = user.store.storeId;
          setCurrentStore({ _id: user.store.storeId, storeId: user.store.storeId });
        } else {
          // Fallback to the storeId from credentials
          axiosInstance.defaults.headers.common['x-storeid'] = credentials.storeId;
          setCurrentStore({ _id: credentials.storeId, storeId: credentials.storeId });
        }
        
        return user;
      } else {
        // For owner login, no store ID needed in headers
        const response = await axiosInstance.post('/auth/login', credentials);
        const user = response.data;

        // Initialize store context for owners
        if (user.role === 'Owner') {
          const firstStore = initializeStoreContext(user);
          if (firstStore) {
            dispatch(setSelectedStore(firstStore));
          }
        }

        return user;
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue('An unexpected error occurred');
      }
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await axiosInstance.post('/auth/logout');
  return {};
});

// Check if session is still valid (called on app load)
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/auth/verify-session');
      return response.data.user;
    } catch (error) {
      // Session expired or invalid
      localStorage.removeItem('user');
      localStorage.removeItem('selectedStore');
      return rejectWithValue('Session expired');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    status: 'idle',
    error: null,
    sessionChecked: false,
  },
  reducers: {
    clearAuth: (state) => {
      state.user = null;
      state.status = 'idle';
      state.error = null;
      localStorage.removeItem('user');
      localStorage.removeItem('selectedStore');
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signup.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.status = 'succeeded';
        window.location.pathname = "login"
      })
      .addCase(signup.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(login.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
        state.sessionChecked = true;
        localStorage.setItem('user', JSON.stringify(action.payload));
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.sessionChecked = true;
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = null;
        state.sessionChecked = false;
        localStorage.removeItem('user');
        localStorage.removeItem('selectedStore');
      })
      .addCase(checkAuth.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
        state.sessionChecked = true;
        localStorage.setItem('user', JSON.stringify(action.payload));
      })
      .addCase(checkAuth.rejected, (state) => {
        state.status = 'idle';
        state.user = null;
        state.sessionChecked = true;
        localStorage.removeItem('user');
        localStorage.removeItem('selectedStore');
      });
  },
});

export const { clearAuth } = authSlice.actions;

export default authSlice.reducer;
